/**
 * The model optimizer's "Details" section: the full evidence behind the result
 * — every budget check, model stats, texture images, glTF extensions, and a
 * log of what optimizing changed. Collapsed by default; the result panel and
 * the fix list above carry what a creator needs to act.
 */
import { isNonPowerOfTwo, rateExtension } from 'trace-viewer/model-utils';
import type { ExtensionSupport, ModelProbeReport, ModelSummary, OptimizationChangeLog, TextureChange, TextureInfo } from 'trace-viewer/model-utils';
import { escapeHtml, formatFileSize, renderChangeRow as changeRow, renderChecks, renderDisclosure, renderKv } from '../shared/ui';

/** Plain-language names for each budget check. */
export const CHECK_LABELS: Record<string, string> = {
  analysis: 'Could be read',
  fileSize: 'File size',
  vertexCount: 'Mesh complexity',
  textureResolution: 'Texture size',
  textureFormat: 'Texture format',
  materialCount: 'Number of materials',
};

// ─── Stats ─────────────────────────────────────────────────────────────────

function renderStats(report: ModelProbeReport): string {
  const g = report.geometry;
  const t = report.textures;
  const rows: Array<[string, string]> = [
    ['File size', formatFileSize(report.fileSize)],
    ['Triangles', Math.round(g.triangleCount).toLocaleString()],
    ['Vertices', g.vertexCount.toLocaleString()],
    ['Meshes', g.meshCount.toString()],
    ['Materials', report.materials.count.toString()],
    ['Textures', t.textureCount.toString()],
  ];

  // Where the bytes go. Guard a zero size (empty/failed read) against NaN.
  const fileSize = report.fileSize;
  const geometry = g.sizeBytes ?? 0;
  const textures = t.totalSize;
  const other = Math.max(0, fileSize - geometry - textures);
  const pct = (n: number): number => (fileSize > 0 ? Math.round((n / fileSize) * 100) : 0);
  const parts: Array<[string, number, string]> = [
    ['Mesh', geometry, '#93a4ff'],
    ['Textures', textures, '#e58fb8'],
    ['Other', other, '#6c7381'],
  ];
  const bar = parts.map(([name, bytes, color]) =>
    `<div style="flex:${pct(bytes)};background:${color};" title="${name}: ${formatFileSize(bytes)}"></div>`).join('');
  const legend = parts.map(([name, bytes, color]) =>
    `<span style="--swatch:${color};">${name} ${pct(bytes)}% (${formatFileSize(bytes)})</span>`).join('');

  const notes = report.warnings.length
    ? `<p class="setting-help" style="margin-top:1rem;">${report.warnings.map(w => escapeHtml(w.message)).join(' ')}</p>`
    : '';

  return `
    ${renderKv(rows)}
    <div class="composition-bar" aria-hidden="true">${bar}</div>
    <div class="composition-legend">${legend}</div>
    ${notes}
  `;
}

// ─── Texture images ────────────────────────────────────────────────────────

function renderTextureCard(tex: TextureInfo): string {
  const fmt = tex.mimeType === 'image/jpeg' ? 'JPEG' : tex.mimeType === 'image/png' ? 'PNG' : 'Other';
  const size = tex.width && tex.height ? `${tex.width}×${tex.height}` : '';
  const meta = [fmt, size, formatFileSize(tex.sizeBytes), tex.gpuMemoryBytes ? `${formatFileSize(tex.gpuMemoryBytes)} in GPU memory` : '']
    .filter(Boolean).join(', ');
  // Non-power-of-two sizes (e.g. 1000×500) can't mipmap/repeat on some GPUs
  // and may be padded to the next power of two in memory.
  const npot = isNonPowerOfTwo(tex.width, tex.height)
    ? '<div class="texture-warn">Not a power of two. Resize to e.g. 1024×512 for sharper, lighter rendering.</div>'
    : '';
  const caption = `${tex.name} — ${fmt}${size ? `, ${size}` : ''}`;
  // The data URL embeds the image's mimeType straight from the glTF JSON, so it
  // is file-controlled like the name — escape it too.
  const thumb = tex.dataUrl
    ? `<button type="button" class="texture-thumb" data-texture-preview="${escapeHtml(tex.dataUrl)}" data-texture-caption="${escapeHtml(caption)}" title="Enlarge">
         <img src="${escapeHtml(tex.dataUrl)}" alt="${escapeHtml(tex.name)}" />
       </button>`
    : '<div class="texture-thumb empty">No preview</div>';
  return `
    <div style="min-width:0;">
      ${thumb}
      <div class="texture-name">${escapeHtml(tex.name)}</div>
      <div class="texture-meta">${escapeHtml(meta)}</div>
      ${npot}
    </div>`;
}

// ─── Extensions ────────────────────────────────────────────────────────────

const EXT_LABEL: Record<ExtensionSupport, string> = {
  supported: 'Supported',
  partial: 'Partly supported',
  unsupported: 'Not supported',
  unknown: 'Untested',
};

/**
 * The glTF extensions the model uses, each rated for the AR runtime. A model
 * with none is plain glTF 2.0 — the safest case — and says so.
 */
function renderExtensions(used: string[], required: string[] = []): string {
  if (used.length === 0) {
    return '<p class="setting-help" style="margin:0;">None. This is plain glTF 2.0, which renders everywhere.</p>';
  }
  const req = new Set(required);
  const rows = used.map(name => {
    const { support, note } = rateExtension(name);
    // Required is the load-or-not distinction, so it belongs on the row itself.
    const label = req.has(name) && support !== 'supported' ? 'Required, risky' : EXT_LABEL[support];
    const detail = req.has(name) ? `Marked required: viewers without it will refuse the file. ${note}` : note;
    return `
      <li class="ext">
        <span class="ext-rating ${req.has(name) && support !== 'supported' ? 'unsupported' : support}">${label}</span>
        <div><div class="ext-name">${escapeHtml(name)}</div><div class="ext-note">${escapeHtml(detail)}</div></div>
      </li>`;
  }).join('');
  return `<ul class="ext-list">${rows}</ul>`;
}

// ─── What changed ──────────────────────────────────────────────────────────

function renderTextureChange(t: TextureChange): string {
  const size = t.fromWidth === t.toWidth && t.fromHeight === t.toHeight
    ? `${t.toWidth}×${t.toHeight}`
    : `${t.fromWidth}×${t.fromHeight} → ${t.toWidth}×${t.toHeight}`;
  const fmt = t.fromFormat === t.toFormat ? t.toFormat.toUpperCase() : `${t.fromFormat.toUpperCase()} → ${t.toFormat.toUpperCase()}`;
  const diff = t.toBytes - t.fromBytes;
  const delta = t.changed && diff !== 0
    ? `<span class="delta ${diff < 0 ? 'smaller' : 'larger'}">${diff < 0 ? '−' : '+'}${formatFileSize(Math.abs(diff))}</span>`
    : '<span class="delta same">Unchanged</span>';
  const bytes = t.changed ? `${formatFileSize(t.fromBytes)} → ${formatFileSize(t.toBytes)}` : formatFileSize(t.fromBytes);
  return `
    <div class="texture-change">
      <span class="name">${escapeHtml(t.name)}</span>${delta}
      <span class="meta">${escapeHtml(size)}, ${escapeHtml(fmt)}</span><span class="meta right">${escapeHtml(bytes)}</span>
    </div>`;
}

function renderChangeLog(c: OptimizationChangeLog): string {
  const n = (x: number): string => x.toLocaleString();
  const notes = [
    c.quantized ? 'Vertex data stored more compactly (slightly lower precision).' : '',
    c.axisConversion === 'zUpToYUp' ? 'Stood upright (Z-up → Y-up).' : '',
    c.scaleFactor !== 1 ? `Rescaled ×${c.scaleFactor}, baked into the mesh.` : '',
    c.setActiveScene ? 'Marked the first scene as the one to show (none was set).' : '',
  ].filter(Boolean).map(s => `<div class="change-note">${s}</div>`).join('');

  const changedTex = c.textures.filter(t => t.changed).length;
  const textures = c.textures.length === 0 ? '' : `
    <h4 class="subhead">Textures, ${changedTex} of ${c.textures.length} changed</h4>
    ${c.textures.map(renderTextureChange).join('')}`;

  const materials = c.materials.length === 0 ? '' : `
    <h4 class="subhead">Materials, ${c.materials.length} edited</h4>
    ${c.materials.map(m => `<div class="change-row"><span>${escapeHtml(m.name)}</span><span class="muted">${m.edits.map(escapeHtml).join(', ')}</span></div>`).join('')}`;

  const pipeline = !c.pipeline?.length ? '' : `
    <h4 class="subhead">Steps run</h4>
    <ol class="pipeline">${c.pipeline.map(p => `<li>${escapeHtml(p)}</li>`).join('')}</ol>`;

  return `
    <h4 class="subhead">Mesh</h4>
    ${changeRow('Triangles', n(c.triangleBefore), n(c.triangleAfter))}
    ${changeRow('Vertices', n(c.vertexBefore), n(c.vertexAfter))}
    ${changeRow('Mesh data', formatFileSize(c.geometryBytesBefore), formatFileSize(c.geometryBytesAfter))}
    ${notes}
    ${textures}
    ${materials}
    ${pipeline}
  `;
}

// ─── Section ───────────────────────────────────────────────────────────────

export function renderDetails(report: ModelProbeReport, summary: ModelSummary | null, changes: OptimizationChangeLog | null): string {
  const textures = report.textures.textures ?? [];
  const axes = summary?.verdict.axes ?? [];
  return `
    <section class="section">
      <h2 class="section-title">Details</h2>
      ${axes.length ? renderDisclosure('checks', 'All checks', renderChecks(axes, CHECK_LABELS), {
        meta: `${axes.filter(a => a.status === 'green').length} of ${axes.length} pass`,
      }) : ''}
      ${changes ? renderDisclosure('changes', 'What optimizing changed', renderChangeLog(changes)) : ''}
      ${renderDisclosure('stats', 'Original model', renderStats(report), { meta: formatFileSize(report.fileSize) })}
      ${textures.length ? renderDisclosure('texture-images', 'Texture images', `<div class="texture-grid">${textures.map(renderTextureCard).join('')}</div>`, {
        meta: `${textures.length}, ${formatFileSize(report.textures.totalSize)}`,
      }) : ''}
      ${renderDisclosure('extensions', 'glTF extensions', renderExtensions(report.extensionsUsed, report.extensionsRequired), {
        meta: report.extensionsUsed.length ? String(report.extensionsUsed.length) : 'None',
      })}
    </section>
  `;
}
